// Which of the federal answers may be carried from one entry to the next, and
// which may not.
//
// Copying is offered because a driver who was subject to the FMCSRs at one
// carrier almost always was at the next, and re-reading the same two sentences
// four times is how a form gets answered without being read. But it is offered,
// never applied: the driver taps, and only these two move.

import { TERMINATED_REASON } from '../values'

import { certificationsFor } from './validation'
import type { EntryDraft, FieldName } from './validation'

/**
 * The two that describe the kind of work, so they travel.
 *
 * Termination does not: it is a fact about one job ending, and copying it would
 * be asserting something about a different employer. Nor does contact
 * permission — a driver may be happy for us to call one employer and not
 * another, and that is a decision to take per employer rather than inherit.
 */
export const COPYABLE_CERTIFICATIONS = [
  'isDriverSubjectToFMCSRs',
  'isJobDesignatedAsSafetySensitiveFunctionUnderDOT',
] as const

export type CopyableCertification = (typeof COPYABLE_CERTIFICATIONS)[number]

/** What tapping "Same answers as …" would set. Never includes an unanswered one. */
export const copyableAnswers = (
  source: EntryDraft | null | undefined
): Partial<Pick<EntryDraft, CopyableCertification>> => {
  const answers: Partial<Pick<EntryDraft, CopyableCertification>> = {}
  if (!source || source.kind !== 'employment') return answers
  for (const field of COPYABLE_CERTIFICATIONS) {
    const value = source[field]
    if (value === true || value === false) answers[field] = value
  }
  return answers
}

/** Whether the offer is worth making: something to copy, somewhere to put it. */
export const canCopyAnswers = (
  source: EntryDraft | null | undefined,
  target: EntryDraft
): boolean => {
  if (target.kind !== 'employment') return false
  const available = copyableAnswers(source)
  return COPYABLE_CERTIFICATIONS.some(
    (field) => available[field] !== undefined && target[field] == null
  )
}

/**
 * The certifications an entry is being asked for, in the order they are shown.
 *
 * The same list validation uses to decide what is missing — see
 * certificationsFor. Always the same four for employment, so the section does
 * not change shape between entries: permission to make contact is asked of every
 * employer because verification depends on it for past ones too, and termination
 * is shown for a current job already answered rather than left unasked.
 */
export const questionsFor = (draft: EntryDraft): FieldName[] =>
  certificationsFor(draft)

/** "2 of 4 answered" — the count a driver watches while working through them. */
export const answeredCount = (
  draft: EntryDraft
): { answered: number; total: number } => {
  const questions = questionsFor(draft)
  const answered = questions.filter((field) => {
    const value = draft[field as keyof EntryDraft]
    return value === true || value === false
  }).length
  return { answered, total: questions.length }
}

/**
 * Choosing "Terminated / discharged" as the reason for leaving is already an
 * answer to "were you terminated?". Returning undefined rather than false for
 * every other reason matters: leaving a job for better pay says nothing about
 * whether an earlier one ended in dismissal, so the question stays unanswered.
 */
export const terminationFromReason = (
  reason: string | null | undefined
): true | undefined => (reason === TERMINATED_REASON ? true : undefined)

/**
 * Yes, unless this is a job the applicant still holds.
 *
 * Calling a current employer is the case to avoid, so that one starts as no.
 * Every other employer starts as yes. The dates re-derive this: marking the
 * job present, or giving it an end, replaces whatever was selected before.
 */
export const defaultShouldContactEmployer = (
  isCurrent: boolean | null | undefined
): boolean => isCurrent !== true
